/**
 * SOLFATA — CART ADD-ONS (Aug 2026)
 * Wires the Gift Note ($2, solfata.com/products/gift-note) and Shipping
 * Protection ($5, solfata.com/products/shipping-protection) checkboxes in
 * snippets/cart-summary.liquid. Both are real, hidden (UNLISTED,
 * requiresShipping: false) products — see CLAUDE.md notes.
 *
 * This script tag is loaded from layout/theme.liquid, NOT from inside
 * cart-summary.liquid. That snippet's markup is replaced wholesale by
 * morphSection() on every cart update (add/remove/quantity change), and a
 * <script> tag arriving via that kind of DOM patch never executes —
 * browsers only run script tags present in the original parsed document.
 * Loading this once from the stable page shell (same pattern as
 * cart-drawer.js and cart-icon.js) guarantees it actually runs.
 *
 * Integration contract: mirrors assets/component-cart-items.js's own
 * updateQuantity() exactly — POST to /cart/add.js or /cart/change.js with a
 * `sections` param, then dispatch a CartLinesUpdateEvent using
 * CartLinesUpdateEvent.createPromise()/createCartFromAjaxResponse() so the
 * native cart-items-component's document-level listener morphs the drawer
 * itself.
 *
 * IMPORTANT — event delegation, not direct binding. cart-summary.liquid's
 * markup (this add-ons block included) gets replaced wholesale by
 * morphSection() on every cart update, including updates this script itself
 * triggers (e.g. editing the gift-note text mid-session re-renders the
 * section a moment later). A one-time querySelector + addEventListener on
 * the checkboxes loses its binding the instant that happens — confirmed live
 * (a dispatched change event produced zero network activity after a prior
 * morph). Listening on `document` for 'change'/'input' and resolving the
 * actual target via closest() at event time means the binding never goes
 * stale, no matter how many times the markup underneath is replaced.
 *
 * Gift Note field visibility is ALSO backed by a CSS rule in
 * cart-summary.liquid (`.cart-addons__item:has(input:checked) + 
 * .cart-addons__gift-note-field[hidden]`) that mirrors the field.hidden
 * toggle below off the checkbox's live :checked state. That's a second
 * safety net against a cart-wide section morph landing between this
 * handler's `await getCart()` and its `field.hidden = false` (e.g. the
 * add-to-cart drawer's own auto-open refresh, arriving right as someone taps
 * this checkbox), which can hand the handler a detached copy of `field`.
 *
 * Gift Note text is captured as a line item property ("Gift Note"). Shopify's
 * cart API can't edit an existing line's properties in place, so an edited
 * message is handled as remove-old-line + add-new-line with the updated text.
 *
 * FRAUD GUARD (Aug 2026) — neither add-on may sit in the cart alone. Checking
 * either one is blocked unless a real (non-add-on) item is already present,
 * and if the cart's last real item is removed by ANY means — our own code or
 * Shopify's native quantity/remove controls — any orphaned add-on line is
 * auto-removed. The reconcile listener hooks `StandardEvents.cartLinesUpdate`
 * on `document` (confirmed via the real @shopify/events source, resolved
 * through the theme's import map to https://cdn.shopify.com/storefront/
 * standard-events.js): every CartLinesUpdateEvent instance bubbles to
 * document and carries a `.promise` that resolves once the triggering
 * write actually lands, so this fires correctly regardless of who
 * dispatched the update.
 *
 * IMPORTANT — this is a UX safeguard, not a fraud boundary. Shopify's cart
 * endpoints (/cart/add.js, the Storefront API) are public and unauthenticated
 * by design, so this client-side guard cannot stop someone from adding an
 * add-on-only cart directly via those endpoints, bypassing this script
 * entirely. The real, server-side block is a Cart & Checkout Validation
 * Shopify Function (separate from this theme) — see project notes.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

(function () {
  const ADDON_SKUS = ['GIFT-NOTE', 'SHIP-PROTECT'];

  function hasPhysicalItem(cart) {
    return cart.items.some((item) => !ADDON_SKUS.includes(item.sku));
  }

  function sectionIdsToUpdate() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids).join(',');
  }

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok || json.errors) {
      const message = typeof json.errors === 'string' ? json.errors : JSON.stringify(json.errors);
      throw new Error(message || 'Cart update failed');
    }
    return json;
  }

  async function getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  function findLineKey(cart, variantId) {
    const item = cart.items.find((i) => i.variant_id === variantId);
    return item ? item.key : null;
  }

  /**
   * Dispatches the standard CartLinesUpdateEvent contract: fire immediately
   * with a deferred promise (so listeners attached at dispatch time have a
   * live promise to chain onto), then resolve/reject once the real network
   * work finishes.
   */
  function dispatchAndResolve(originEl, action, work) {
    const deferred = CartLinesUpdateEvent.createPromise();

    originEl.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: [],
        promise: deferred.promise,
      })
    );

    work()
      .then(async (sections) => {
        const cart = await getCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            sections,
            items: cart.items,
            itemCount: cart.item_count,
            source: 'cart-addons',
            didError: false,
          },
        });
      })
      .catch((error) => {
        deferred.reject(error);
      });

    return deferred.promise;
  }

  function addLine(originEl, variantId, properties) {
    const sections = sectionIdsToUpdate();
    const items = [{ id: variantId, quantity: 1 }];
    if (properties) items[0].properties = properties;
    return dispatchAndResolve(originEl, 'add', () =>
      cartRequest('/cart/add.js', { items, sections }).then((json) => json.sections)
    );
  }

  function removeLine(originEl, lineKey) {
    const sections = sectionIdsToUpdate();
    return dispatchAndResolve(originEl, 'remove', () =>
      cartRequest('/cart/change.js', { id: lineKey, quantity: 0, sections }).then((json) => json.sections)
    );
  }

  function showError(errorEl, message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.classList.add('is-visible');
  }

  function clearError(errorEl) {
    if (!errorEl) return;
    errorEl.classList.remove('is-visible');
  }

  /**
   * @param {HTMLInputElement} checkbox
   * @param {HTMLElement | null} errorEl
   * @param {boolean} add
   * @param {Record<string, string> | null} properties
   */
  async function setAddonState(checkbox, errorEl, add, properties) {
    checkbox.disabled = true;
    clearError(errorEl);

    try {
      const variantId = parseInt(checkbox.dataset.variantId, 10);
      const cart = await getCart();
      const existingKey = findLineKey(cart, variantId);

      if (add) {
        if (existingKey && !properties) {
          return;
        }
        if (existingKey && properties) {
          await removeLine(checkbox, existingKey);
        }
        await addLine(checkbox, variantId, properties);
      } else if (existingKey) {
        await removeLine(checkbox, existingKey);
      }
    } catch (error) {
      console.warn('[cart-addons]', error);
      showError(errorEl, error instanceof Error ? error.message : 'Something went wrong. Please try again.');
      checkbox.checked = !checkbox.checked;
    } finally {
      checkbox.disabled = false;
    }
  }

  function updateGiftNoteCount(root) {
    const textarea = root.querySelector('[data-gift-note-text]');
    const counter = root.querySelector('[data-gift-note-count]');
    if (!(textarea instanceof HTMLTextAreaElement) || !counter) return;
    const max = textarea.maxLength > 0 ? textarea.maxLength : 200;
    counter.textContent = `${textarea.value.length}/${max}`;
  }

  let giftNoteDebounce;

  document.addEventListener('change', async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') return;

    const checkbox = target.closest('[data-cart-addon]');
    if (!checkbox) return;

    const root = checkbox.closest('[data-cart-addons]');
    if (!root) return;

    const errorEl = root.querySelector('[data-cart-addons-error]');
    const addonType = checkbox.dataset.cartAddon;

    if (checkbox.checked) {
      const cart = await getCart();
      if (!hasPhysicalItem(cart)) {
        checkbox.checked = false;
        showError(
          errorEl,
          'Add a ritual mist or stone anchor to your bag before adding this — it can’t be ordered on its own.'
        );
        return;
      }
    }

    if (addonType === 'shipping-protection') {
      setAddonState(checkbox, errorEl, checkbox.checked, null);
      return;
    }

    if (addonType === 'gift-note') {
      const field = root.querySelector('[data-gift-note-field]');
      const textarea = root.querySelector('[data-gift-note-text]');

      if (checkbox.checked) {
        if (field) field.hidden = false;
        if (textarea) textarea.focus();
        updateGiftNoteCount(root);
        const text = textarea ? textarea.value.trim() : '';
        if (text) setAddonState(checkbox, errorEl, true, { 'Gift Note': text });
      } else {
        if (field) field.hidden = true;
        setAddonState(checkbox, errorEl, false, null);
      }
    }
  });

  let reconciling = false;

  document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
    if (reconciling) return;
    if (!(event instanceof CartLinesUpdateEvent)) return;

    reconciling = true;
    try {
      await event.promise;
    } catch (error) {
      reconciling = false;
      return;
    }

    try {
      const cart = await getCart();
      if (hasPhysicalItem(cart)) return;

      const orphaned = cart.items.filter((item) => ADDON_SKUS.includes(item.sku));
      if (!orphaned.length) return;

      for (const item of orphaned) {
        await removeLine(document.body, item.key);
      }
    } catch (error) {
      console.warn('[cart-addons] reconcile failed', error);
    } finally {
      reconciling = false;
    }
  });

  document.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLTextAreaElement) || !target.matches('[data-gift-note-text]')) return;

    const root = target.closest('[data-cart-addons]');
    if (!root) return;

    updateGiftNoteCount(root);

    const checkbox = root.querySelector('[data-cart-addon="gift-note"]');
    const errorEl = root.querySelector('[data-cart-addons-error]');
    if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) return;

    clearTimeout(giftNoteDebounce);
    giftNoteDebounce = setTimeout(async () => {
      const text = target.value.trim();

      if (text) {
        await setAddonState(checkbox, errorEl, true, { 'Gift Note': text });
      } else {
        // Text cleared while checked — remove the line but leave the
        // checkbox/field open so they can keep typing.
        const variantId = parseInt(checkbox.dataset.variantId, 10);
        const cart = await getCart();
        const existingKey = findLineKey(cart, variantId);
        if (existingKey) await removeLine(checkbox, existingKey);
      }
    }, 600);
  });
})();
